import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/context/AuthContext";
import { inicioPiloto } from "@/lib/inicio";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Package,
  Truck,
  Users,
  ClipboardList,
  Factory,
  Scissors,
  PackageCheck,
  MapPinned,
  Receipt,
  UserCog,
  LogOut,
  BarChart3,
  Bell,
  Navigation,
  Tag,
  Landmark,
  Scale,
  Route,
  FileSpreadsheet,
  Clock,
  Wallet,
  Boxes,
  Camera,
  XCircle,
  Receipt as ReceiptIcon2,
  Store,
  TrendingUp,
  PanelLeftClose,
  PanelLeft,
  Lock,
  BedDouble,
  AlertTriangle,
} from "lucide-react";

const NAV_FIXED_FIRST = ["/panel", "/pedidos", "/productos"];

// Preferencia de ruta inicial cuando el user no tiene dashboard (mismo mapa que Login)
const MODULO_FIRST_ROUTE_MAP = {
  pedidos: "/pedidos", fabricacion: "/fabricacion", costuras: "/costuras", bases: "/bases", alertas: "/alertas", empaque: "/empaque",
  entregas: "/entrega", facturacion: "/facturacion", conciliacion: "/conciliacion",
  reportes: "/reportes", plataforma_guias: "/plataforma-guias", inventario: "/inventario",
  gastos: "/gastos", marcaje: "/marcaje", clientes: "/clientes", productos: "/productos",
  usuarios: "/usuarios", cuentas: "/cuentas", domicilios: "/domicilios",
  cuadre: "/cuadre-repartidor", cierre_mes: "/cierre-mes", seguimiento: "/seguimiento", organizador: "/organizador-paquetes",
  landing: "/landing-admin", dashboard: "/panel",
};
function firstAllowedRouteFor(user) {
  if (!user) return "/login";
  if (user.role === "piloto") return inicioPiloto(user);
  const mods = Array.isArray(user.modulos) ? user.modulos : [];
  if (mods.includes("dashboard") || user.role === "admin") return "/panel";
  const preferido = ["pedidos", "fabricacion", "empaque", "entregas", "domicilios",
    "cuadre", "facturacion", "conciliacion", "reportes", "cuentas", "gastos",
    "marcaje", "inventario", "plataforma_guias", "productos", "clientes",
    "seguimiento", "landing", "cierre_mes", "usuarios"];
  for (const m of preferido) if (mods.includes(m) && MODULO_FIRST_ROUTE_MAP[m]) return MODULO_FIRST_ROUTE_MAP[m];
  for (const m of mods) if (MODULO_FIRST_ROUTE_MAP[m]) return MODULO_FIRST_ROUTE_MAP[m];
  return "/login";
}

const NAV = [
  { to: "/panel", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "piloto", "facturacion"], modulo: "dashboard" },
  { to: "/pedidos", label: "Hoja de Pedidos", icon: ClipboardList, roles: ["admin", "vendedor"], modulo: "pedidos" },
  { to: "/mis-ventas", label: "Mis Ventas", icon: BarChart3, roles: ["vendedor"], modulo: "pedidos" },
  { to: "/productos", label: "Catálogo", icon: Package, roles: ["admin"], modulo: "productos" },
  { to: "/promociones", label: "Promociones", icon: Tag, roles: ["admin"], modulo: "productos" },
  { to: "/envios", label: "Métodos de Envío", icon: Truck, roles: ["admin"], modulo: "cuentas" },
  { to: "/transportistas", label: "Empresas de Transporte", icon: Truck, roles: ["admin"], modulo: "cuentas" },
  { to: "/cajas", label: "Cajas", icon: Wallet, roles: ["admin", "facturacion", "gerente_admin"], modulo: "cuentas" },
  { to: "/modificadores", label: "Modificadores", icon: Tag, roles: ["admin"], modulo: "productos" },
  { to: "/cxc-facturada", label: "CxC Facturada", icon: Receipt, roles: ["admin", "facturacion", "gerente_admin"], modulo: "reportes" },
  { to: "/reporte-visual", label: "Reporte Visual", icon: BarChart3, roles: ["admin", "facturacion", "gerente_admin"], modulo: "reportes" },
  { to: "/fotos-pagos", label: "Fotos de Pagos", icon: Camera, roles: ["admin", "facturacion", "gerente_admin"], modulo: "reportes" },
  { to: "/facturas-gastos", label: "Facturas de Gastos", icon: Receipt, roles: ["admin", "facturacion", "gerente_admin"], modulo: "gastos" },
  { to: "/pedidos-cancelados", label: "Pedidos Cancelados", icon: XCircle, roles: ["admin", "facturacion", "gerente_admin"], modulo: "reportes" },
  { to: "/flujo-facturados", label: "Flujo Facturados", icon: Receipt, roles: ["admin", "facturacion", "gerente_admin"], modulo: "facturacion" },
  { to: "/metodos-pago", label: "Métodos de Pago", icon: Landmark, roles: ["admin", "facturacion"], modulo: "cuentas" },
  { to: "/conciliacion", label: "Conciliación", icon: Scale, roles: ["admin", "facturacion"], modulo: "conciliacion" },
  { to: "/clientes", label: "Clientes", icon: Users, roles: ["admin"], modulo: "clientes" },
  { to: "/fabricacion", label: "Fabricación", icon: Factory, roles: ["admin", "fabrica"], modulo: "fabricacion" },
  { to: "/costuras", label: "Costuras", icon: Scissors, roles: ["admin", "fabrica"], modulo: "costuras" },
  { to: "/bases", label: "Bases", icon: BedDouble, roles: ["admin", "fabrica", "gerente_admin"], modulo: "bases" },
  { to: "/alertas", label: "Alertas", icon: AlertTriangle, roles: ["admin", "fabrica", "gerente_admin"], modulo: "alertas" },
  { to: "/empaque", label: "Entregas Departamento", icon: PackageCheck, roles: ["admin"], modulo: "empaque" },
  { to: "/entrega", label: "Entregas Ciudad", icon: MapPinned, roles: ["admin"], modulo: "entregas" },
  { to: "/reporte-ruta", label: "Reporte de Ruta", icon: Route, roles: ["admin", "facturacion", "piloto"], modulo: "reportes" },
  { to: "/domicilios", label: "App Domicilios", icon: Truck, roles: ["admin", "gerente_admin", "piloto"], modulo: "domicilios" },
  { to: "/cuadre-repartidor", label: "Cuadre Repartidor", icon: ClipboardList, roles: ["admin", "gerente_admin", "facturacion"], modulo: "cuadre" },
  { to: "/ruta-mapa", label: "Mapa Ruta Día", icon: MapPinned, roles: ["admin", "gerente_admin"], modulo: "reportes" },
  { to: "/cierre-mes", label: "Cierre de Mes", icon: Lock, roles: ["admin"], modulo: "cierre_mes" },
  { to: "/app-empaque", label: "App Empaque", icon: Route, roles: ["admin", "fabrica", "facturacion", "gerente_admin"], modulo: "empaque" },
  { to: "/organizador-paquetes", label: "Organizador de Paquetes", icon: Boxes, roles: ["admin", "fabrica", "facturacion", "gerente_admin", "piloto", "vendedor"], modulo: "organizador" },
  { to: "/app-colocacion", label: "Recolección y Colocación", icon: Route, roles: ["admin", "fabrica", "facturacion", "gerente_admin", "piloto", "vendedor"], modulo: "colocacion" },
  { to: "/app-seguimiento", label: "Seguimiento y Entregas", icon: Route, roles: ["admin", "vendedor", "fabrica", "piloto", "facturacion", "gerente_admin"], modulo: "seguimiento" },
  { to: "/seguimiento", label: "Seguimiento (tabla)", icon: Route, roles: ["admin", "vendedor", "fabrica", "piloto", "facturacion"], modulo: "seguimiento" },
  { to: "/historial-reportes", label: "Historial Reportes", icon: FileSpreadsheet, roles: ["admin", "facturacion"], modulo: "reportes" },
  { to: "/historial-entregas", label: "Historial de Entregas", icon: Truck, roles: ["admin", "facturacion", "gerente_admin"], modulo: "entregas" },
  { to: "/facturacion", label: "Facturación", icon: Receipt, roles: ["admin", "facturacion"], modulo: "facturacion" },
  { to: "/cierre-automatico", label: "Cierre Automático", icon: Scale, roles: ["admin", "facturacion"], modulo: "conciliacion" },
  { to: "/gastos", label: "Gastos / Proveedores", icon: ReceiptIcon2, roles: ["admin", "facturacion"], modulo: "gastos" },
  { to: "/plataforma-guias", label: "Plataforma de Guías", icon: Truck, roles: ["admin", "facturacion"], modulo: "plataforma_guias" },
  { to: "/inventario", label: "Inventario", icon: Boxes, roles: ["admin", "fabrica", "facturacion"], modulo: "inventario" },
  { to: "/cuentas", label: "Libro de Cuentas", icon: Wallet, roles: ["admin", "facturacion"], modulo: "cuentas" },
  { to: "/mi-dinero", label: "Mi Dinero", icon: Wallet, roles: ["admin", "gerente_admin"] },
  { to: "/resumen-pagos", label: "Resumen de Pagos", icon: ReceiptIcon2, roles: ["admin", "facturacion"], modulo: "reportes" },
  { to: "/marcaje", label: "Marcaje", icon: Clock, roles: ["admin", "vendedor", "piloto", "facturacion"], modulo: "marcaje" },
  { to: "/reportes", label: "Reportes", icon: BarChart3, roles: ["admin", "facturacion"], modulo: "reportes" },
  { to: "/reporte-vendedoras", label: "Reporte Vendedoras", icon: Users, roles: ["admin", "gerente_admin", "facturacion"], modulo: "reportes" },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell, roles: ["admin"], modulo: "dashboard" },
  { to: "/landing-admin", label: "Tienda Online", icon: Store, roles: ["admin", "gerente_admin"], modulo: "landing" },
  { to: "/landing-analytics", label: "Analítica Landing", icon: TrendingUp, roles: ["admin", "gerente_admin"], modulo: "landing" },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, roles: ["admin"], modulo: "usuarios" },
];

// Orden final: Dashboard → Hoja de Pedidos → Catálogo → resto alfabético.
function sortNavAlpha(items) {
  const fixed = NAV_FIXED_FIRST
    .map((path) => items.find((n) => n.to === path))
    .filter(Boolean);
  const rest = items
    .filter((n) => !NAV_FIXED_FIRST.includes(n.to))
    .slice()
    .sort((a, b) => (a.label || "").localeCompare(b.label || "", "es", { sensitivity: "base" }));
  return [...fixed, ...rest];
}

const ROLE_LABEL = {
  admin: "Administrador",
  gerente_admin: "Gerente Administrador",
  vendedor: "Vendedor",
  fabrica: "Fábrica",
  piloto: "Piloto",
  facturacion: "Facturación",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Ronda 17 — Permisos individuales primero:
  // Si el usuario tiene `modulos` custom (lista blanca), esa lista es la
  // ÚNICA fuente de verdad — ignora restricciones por rol. Si no tiene lista,
  // se usa el rol como antes.
  const userModulos = user?.modulos;
  const usesCustom = Array.isArray(userModulos) && userModulos.length > 0;
  const homeRoute = firstAllowedRouteFor(user);
  const links = sortNavAlpha(NAV.filter((n) => {
    if (user?.role === "admin") return true;
    if (usesCustom) return !n.modulo || userModulos.includes(n.modulo);
    return hasRole(user, ...n.roles);
  }));

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 flex">
      {/* Sidebar (colapsable en desktop) */}
      <aside className={`hidden md:flex flex-col bg-white border-r border-zinc-200 sticky top-0 h-screen transition-all duration-200 ${collapsed ? "md:w-14" : "md:w-64 lg:w-72"}`}>
        <div className={`px-6 pt-8 pb-6 border-b border-zinc-200 flex items-center justify-between gap-2 ${collapsed ? "px-2 pt-4 pb-2" : ""}`}>
          {!collapsed && (
            <Link to={homeRoute} data-testid="brand-link" className="block flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Ventas ERP</div>
              <div className="mt-1 font-manrope font-black text-2xl tracking-tight leading-none">
                Hoja de <span className="text-[#002FA7]">Pedidos</span>
              </div>
            </Link>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-600 shrink-0"
            title={collapsed ? "Expandir menú" : "Ocultar menú"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <ul className="space-y-1">
            {links.map((n) => {
              const Icon = n.icon;
              return (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={collapsed ? n.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ease-out ${
                        isActive
                          ? "bg-zinc-900 text-white"
                          : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                      } ${collapsed ? "justify-center px-2" : ""}`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {!collapsed && <span className="truncate">{n.label}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={`border-t border-zinc-200 p-4 ${collapsed ? "p-2" : ""}`}>
          {!collapsed && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#002FA7] text-white flex items-center justify-center font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                  {ROLE_LABEL[user?.role] || user?.role}
                </div>
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${collapsed ? "px-2" : "justify-start gap-2"}`}
            onClick={doLogout}
            data-testid="logout-btn"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && "Cerrar sesión"}
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="font-manrope font-black tracking-tight text-lg">
          Hoja de <span className="text-[#002FA7]">Pedidos</span>
        </div>
        <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn-mobile">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 mt-14 md:mt-0">
        <div className="max-w-[1400px] mx-auto p-4 md:p-8 lg:p-10">
          <Outlet />
        </div>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-zinc-200 flex overflow-x-auto z-40">
          {links.slice(0, 5).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `flex-1 min-w-[70px] py-2 flex flex-col items-center gap-0.5 text-[10px] ${
                    isActive ? "text-[#002FA7]" : "text-zinc-500"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{n.label.split(" ")[0]}</span>
              </NavLink>
            );
          })}
        </nav>
      </main>
    </div>
  );
}
